import { Component } from '@theme/component';

/**
 * ANSI guide (`sections/pc-guia-ansi.liquid`): loads the models of a cut level under the levels table.
 *
 * Every table row links to the collection filtered by that level, which works without JavaScript. Here the click is
 * intercepted and the cards come from `sections/pc-guia-ansi-modelos.liquid`, rendered on the same filtered
 * collection URL (Section Rendering API), so the page never lists the catalog itself. On load it opens the level of
 * `?nivel=` or, without it, the lowest level that has models.
 *
 * @typedef {object} Refs
 * @property {HTMLScriptElement} strings - JSON with the translated texts.
 * @property {HTMLElement} panel - Models region.
 * @property {HTMLElement} title - Heading of the region, focused after a click.
 * @property {HTMLElement} count - Number of models of the level.
 * @property {HTMLElement} status - Live region.
 * @property {HTMLElement} list - Cards.
 * @property {HTMLAnchorElement} all - Link to the whole filtered collection.
 * @property {HTMLAnchorElement[]} [levels] - Row links, one per level.
 *
 * @extends {Component<Refs>}
 */
class PcAnsiGuide extends Component {
  requiredRefs = ['strings', 'panel', 'title', 'count', 'status', 'list', 'all'];

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {AbortController | undefined} */
  #request;

  /** @type {Record<string, string>} */
  #strings = {};

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    this.#strings = JSON.parse(this.refs.strings.textContent || '{}');

    for (const link of this.refs.levels ?? []) {
      link.addEventListener('click', this.#onLevelClick, { signal: this.#abort.signal });
    }

    void this.#open();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abort?.abort();
    this.#abort = undefined;
    this.#request?.abort();
  }

  /**
   * @param {string} template
   * @param {Record<string, string | number>} values
   */
  #fill(template, values) {
    return template.replace(/__(\w+)__/g, (match, key) => (key in values ? String(values[key]) : match));
  }

  /** @param {MouseEvent} event */
  #onLevelClick = (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;

    const level = event.currentTarget instanceof HTMLElement ? event.currentTarget.dataset.level : undefined;
    if (!level) return;

    event.preventDefault();
    void this.#show(level, { focus: true });
  };

  /** Opens the level of the URL, or the lowest one with models. */
  async #open() {
    const levels = (this.dataset.levels ?? '').split(',').filter(Boolean);
    const wanted = new URLSearchParams(window.location.search).get('nivel');

    if (wanted && levels.includes(wanted)) {
      await this.#show(wanted);
      return;
    }

    for (const level of levels) {
      const total = await this.#show(level, { quiet: true });
      if (total === undefined) return;
      if (total > 0) return;
    }

    this.refs.title.textContent = this.#strings.noneAny;
    this.refs.count.textContent = '';
    this.refs.panel.hidden = false;
  }

  /**
   * Loads and renders the models of a level.
   * @param {string} level
   * @param {{ focus?: boolean, quiet?: boolean }} [options] - `quiet`: do not render an empty level.
   * @returns {Promise<number | undefined>} Number of models, or undefined when the request failed or was superseded.
   */
  async #show(level, { focus = false, quiet = false } = {}) {
    const { title, count, status, list, all, panel } = this.refs;
    const base = this.dataset.collectionUrl ?? '/collections/all';
    const param = this.dataset.filterParam ?? '';

    const filtered = new URL(base, window.location.origin);
    filtered.searchParams.set(param, level);

    const url = new URL(filtered);
    url.searchParams.set('section_id', this.dataset.modelsSection ?? '');

    this.#request?.abort();
    this.#request = new AbortController();
    const { signal } = this.#request;

    list.setAttribute('aria-busy', 'true');
    status.textContent = this.#strings.loading;

    try {
      const response = await fetch(url, { signal });
      if (!response.ok) throw new Error(String(response.status));

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const models = html.querySelector('[data-pc-ansi-models]');
      if (!models) throw new Error('missing models');

      const total = Number(models.dataset.total) || 0;
      if (total === 0 && quiet) return 0;

      const cards = [...models.children];
      list.replaceChildren(...cards);
      panel.hidden = false;
      list.setAttribute('aria-busy', 'false');

      title.textContent = this.#fill(this.#strings.heading, { level });
      count.textContent =
        total === 0
          ? this.#fill(this.#strings.none, { level })
          : this.#fill(total === 1 ? this.#strings.countOne : this.#strings.countOther, { qty: total });
      status.textContent = `${title.textContent}. ${count.textContent}`;

      const shown = list.querySelectorAll('.pc-ansi-models__item').length;
      all.hidden = total <= shown;
      if (total > shown) {
        all.href = filtered.pathname + filtered.search;
        all.textContent = this.#fill(this.#strings.viewAll, { qty: total });
      }

      for (const link of this.refs.levels ?? []) {
        if (link.dataset.level === level) link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      }

      const next = new URL(window.location.href);
      next.searchParams.set('nivel', level);
      window.history.replaceState(window.history.state, '', next);

      if (focus) {
        panel.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
        title.focus({ preventScroll: true });
      }

      return total;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return undefined;

      list.setAttribute('aria-busy', 'false');
      panel.hidden = false;
      title.textContent = this.#fill(this.#strings.heading, { level });
      count.textContent = this.#strings.error;
      status.textContent = this.#strings.error;
      return undefined;
    }
  }
}

if (!customElements.get('pc-ansi-guide')) {
  customElements.define('pc-ansi-guide', PcAnsiGuide);
}
